import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  confirmSignIn,
  getCurrentUser,
  fetchAuthSession,
  associateWebAuthnCredential,
  listWebAuthnCredentials,
} from 'aws-amplify/auth';
import { normalizeEmail } from '@synphant/core/normalize-email';

export type AuthPage = 'auth' | 'dashboard' | 'cli-callback';

export interface AuthUser {
  userId: string;
  email: string;
}

export interface CliLoginParams {
  port: number;
  state: string;
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  email: string | null;
  accessToken: string | null;
  idToken: string | null;
  currentPage: AuthPage;
  cliLoginParams: CliLoginParams | null;
  error: string | null;
  hasPasskey: boolean;

  // Auth actions
  initiateAuth: (email: string) => Promise<void>;
  signInWithPasskey: () => Promise<void>;
  sendVerificationCode: () => Promise<void>;
  verifyCode: (code: string) => Promise<{ hasPasskey: boolean }>;
  registerPasskey: () => Promise<void>;
  skipPasskey: () => void;
  signOut: () => Promise<void>;
  clearError: () => void;

  // Navigation
  navigateTo: (page: AuthPage) => void;
  setCliLoginParams: (params: CliLoginParams) => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}

export function AuthProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [email, setEmail] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<AuthPage>('auth');
  const [error, setError] = useState<string | null>(null);
  const [hasPasskey, setHasPasskey] = useState(false);

  // Read CLI params synchronously before any async work so they are available
  // during checkSession — even when the user already has an active Cognito session
  // and AuthPage never renders.
  const initialCliParams = ((): CliLoginParams | null => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('cli') !== 'true') return null;
    const port = params.get('port');
    const state = params.get('state');
    if (!port) throw new Error('CLI login requires port parameter');
    if (!state) throw new Error('CLI login requires state parameter');
    return { port: Number(port), state };
  })();

  const [cliLoginParams, setCliLoginParamsState] = useState<CliLoginParams | null>(initialCliParams);
  const cliLoginParamsRef = useRef<CliLoginParams | null>(initialCliParams);

  const setCliLoginParams = useCallback((params: CliLoginParams) => {
    cliLoginParamsRef.current = params;
    setCliLoginParamsState(params);
  }, []);

  useEffect(() => {
    void checkSession();
  }, []);

  async function checkSession(): Promise<void> {
    setIsLoading(true);
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      if (!token) {
        throw new Error('No access token in session');
      }
      const idTokenValue = session.tokens?.idToken?.toString();
      if (!idTokenValue) {
        throw new Error('No ID token in session');
      }
      const loginId = currentUser.signInDetails?.loginId;
      if (!loginId) {
        throw new Error('No login email found in current session');
      }
      setUser({ userId: currentUser.userId, email: loginId });
      setEmail(loginId);
      setAccessToken(token);
      setIdToken(idTokenValue);
      setIsAuthenticated(true);
      setCurrentPage(cliLoginParamsRef.current ? 'cli-callback' : 'dashboard');
    } catch {
      setUser(null);
      setAccessToken(null);
      setIdToken(null);
      setIsAuthenticated(false);
      setCurrentPage('auth');
    } finally {
      setIsLoading(false);
    }
  }

  async function applySession(authenticatedEmail: string): Promise<void> {
    const currentUser = await getCurrentUser();
    const session = await fetchAuthSession();
    const token = session.tokens?.accessToken?.toString();
    if (!token) {
      throw new Error('No access token in session after sign-in');
    }
    const idTokenValue = session.tokens?.idToken?.toString();
    if (!idTokenValue) {
      throw new Error('No ID token in session after sign-in');
    }
    setUser({ userId: currentUser.userId, email: authenticatedEmail });
    setEmail(authenticatedEmail);
    setAccessToken(token);
    setIdToken(idTokenValue);
    setIsAuthenticated(true);
    setCurrentPage(cliLoginParamsRef.current ? 'cli-callback' : 'dashboard');
  }

  const initiateAuth = useCallback(async (email: string): Promise<void> => {
    const normalized = normalizeEmail(email);
    setEmail(normalized);
    setError(null);
    try {
      await amplifySignIn({
        username: normalized,
        options: { authFlowType: 'CUSTOM_WITHOUT_SRP' },
      });
    } catch (err: unknown) {
      if (err instanceof Error && (err.name === 'UserNotFoundException' || err.message.includes('User does not exist'))) {
        const remapped = new Error('Unable to sign in. Please check your email and try again.');
        setError(remapped.message);
        throw remapped;
      }
      setError(err instanceof Error ? err.message : 'Failed to sign in');
      throw err;
    }
  }, []);

  const signInWithPasskey = useCallback(async (): Promise<void> => {
    if (!email) {
      throw new Error('No email set — call initiateAuth first');
    }
    const currentEmail = normalizeEmail(email);
    try {
      setError(null);
      const result = await amplifySignIn({
        username: currentEmail,
        options: { authFlowType: 'USER_AUTH', preferredChallenge: 'WEB_AUTHN' },
      });
      if (!result.isSignedIn) {
        throw new Error(`Unexpected sign-in step after passkey: ${result.nextStep?.signInStep}`);
      }
      await applySession(currentEmail);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Passkey sign-in failed');
      // Re-initiate CUSTOM_WITHOUT_SRP so the user can fall back to email OTP.
      // The USER_AUTH flow above replaced the original auth session; without this,
      // sendVerificationCode's confirmSignIn would hit a dead session.
      try {
        await amplifySignIn({
          username: currentEmail,
          options: { authFlowType: 'CUSTOM_WITHOUT_SRP' },
        });
      } catch {
        /* If re-initiation fails, user will need to start over */
      }
      throw err;
    }
  }, [email]);

  const sendVerificationCode = useCallback(async (): Promise<void> => {
    try {
      setError(null);
      await confirmSignIn({ challengeResponse: 'EMAIL_OTP' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send verification code');
      throw err;
    }
  }, []);

  const verifyCode = useCallback(async (code: string): Promise<{ hasPasskey: boolean }> => {
    if (!email) {
      throw new Error('No email set — call initiateAuth first');
    }
    const currentEmail = normalizeEmail(email);
    try {
      setError(null);
      const result = await confirmSignIn({ challengeResponse: code });
      if (!result.isSignedIn) {
        throw new Error(`Unexpected sign-in step after code: ${result.nextStep?.signInStep}`);
      }
      // Check passkeys BEFORE applySession — applySession triggers navigation
      // to cli-callback, so any async work after it creates a race condition
      // where CliLoginCallback mounts, redirects, then gets remounted.
      const credentials = await listWebAuthnCredentials();
      const userHasPasskey = credentials.credentials.length > 0;
      setHasPasskey(userHasPasskey);
      await applySession(currentEmail);
      return { hasPasskey: userHasPasskey };
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Code verification failed');
      throw err;
    }
  }, [email]);

  const registerPasskey = useCallback(async (): Promise<void> => {
    try {
      setError(null);
      await associateWebAuthnCredential();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Passkey registration failed');
      throw err;
    }
  }, []);

  const skipPasskey = useCallback((): void => {
    setCurrentPage(cliLoginParamsRef.current ? 'cli-callback' : 'dashboard');
  }, []);

  const signOut = useCallback(async (): Promise<void> => {
    await amplifySignOut();
    setUser(null);
    setEmail(null);
    setAccessToken(null);
    setIdToken(null);
    setIsAuthenticated(false);
    setCurrentPage('auth');
    setError(null);
    setHasPasskey(false);
  }, []);

  const navigateTo = useCallback((page: AuthPage) => {
    setCurrentPage(page);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated,
      isLoading,
      email,
      accessToken,
      idToken,
      currentPage,
      cliLoginParams,
      error,
      hasPasskey,
      initiateAuth,
      signInWithPasskey,
      sendVerificationCode,
      verifyCode,
      registerPasskey,
      skipPasskey,
      signOut,
      clearError,
      navigateTo,
      setCliLoginParams,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
